package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBlock
import co.maytes.standupbot.dashboard.components.feedback.SkeletonBox
import co.maytes.standupbot.dashboard.components.feedback.SkeletonCard
import co.maytes.standupbot.dashboard.components.feedback.SkeletonFormFields
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.features.standups.rememberStandupsStore
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.shared.dto.template.CreateTemplateRequestDto
import co.maytes.standupbot.shared.dto.template.UpdateTemplateRequestDto
import co.maytes.standupbot.shared.template.SectionsConfigDto
import co.maytes.standupbot.shared.validation.SectionsConfigValidator
import co.maytes.standupbot.shared.validation.ValidationResult
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.events.Event

sealed interface TemplateEditorMode {
    data class Create(val seedFromDefault: Boolean) : TemplateEditorMode
    data class Edit(val templateId: String) : TemplateEditorMode
}

private data class TemplateFormSnapshot(
    val name: String,
    val description: String,
    val promptBody: String,
    val sectionsConfig: SectionsConfigDto,
) {
    fun differsFrom(
        name: String,
        description: String,
        promptBody: String,
        sectionsConfig: SectionsConfigDto,
    ): Boolean =
        this.name != name ||
            this.description != description ||
            this.promptBody != promptBody ||
            this.sectionsConfig != sectionsConfig
}

@Composable
fun TemplateEditor(
    workspaceId: String,
    mode: TemplateEditorMode,
    store: TemplatesStore,
) {
    val ctx = rememberPageContext()
    val ui = rememberUiStore()
    val teamsStore = rememberStandupsStore()
    val auth = rememberAuthStore()
    val scope = rememberCoroutineScope()
    val workspaceName = auth.currentUser?.workspace?.name

    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var promptBody by remember { mutableStateOf("") }
    var sectionsConfig by remember { mutableStateOf(SectionsConfigDto.DEFAULT) }
    var activePresetId: String? by remember { mutableStateOf(null) }
    var loaded by remember(mode) {
        val initial = when (mode) {
            is TemplateEditorMode.Create -> !mode.seedFromDefault
            is TemplateEditorMode.Edit -> false
        }
        mutableStateOf(initial)
    }
    var isDefault by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var testFireOpen by remember { mutableStateOf(false) }
    var baseline by remember(mode) {
        mutableStateOf(TemplateFormSnapshot("", "", "", SectionsConfigDto.DEFAULT))
    }
    var suppressGuard by remember { mutableStateOf(false) }

    LaunchedEffect(mode, workspaceId) {
        when (mode) {
            is TemplateEditorMode.Create -> if (mode.seedFromDefault) {
                when (val res = store.refresh(workspaceId)) {
                    is Result.Ok -> {
                        val default = res.value.firstOrNull { it.isDefault }
                        if (default != null) {
                            name = "${default.name} (copy)"
                            description = default.description.orEmpty()
                            promptBody = default.promptBody
                            sectionsConfig = default.sectionsConfig
                        }
                        baseline = TemplateFormSnapshot(name, description, promptBody, sectionsConfig)
                        loaded = true
                    }
                    is Result.Err -> {
                        ui.showToast("Could not load default: ${res.error.displayMessage}", ToastKind.Error)
                        baseline = TemplateFormSnapshot(name, description, promptBody, sectionsConfig)
                        loaded = true
                    }
                }
            } else {
                baseline = TemplateFormSnapshot(name, description, promptBody, sectionsConfig)
            }
            is TemplateEditorMode.Edit -> {
                when (val res = store.get(workspaceId, mode.templateId)) {
                    is Result.Ok -> {
                        val t = res.value
                        name = t.name
                        description = t.description.orEmpty()
                        promptBody = t.promptBody
                        sectionsConfig = t.sectionsConfig
                        isDefault = t.isDefault
                        baseline = TemplateFormSnapshot(name, description, promptBody, sectionsConfig)
                        loaded = true
                    }
                    is Result.Err -> {
                        ui.showToast("Template not found", ToastKind.Error)
                        ctx.router.navigateTo("/app/templates")
                    }
                }
            }
        }
    }

    val isDirty = loaded && !isDefault && !suppressGuard && baseline.differsFrom(
        name,
        description,
        promptBody,
        sectionsConfig,
    )

    DisposableEffect(isDirty) {
        if (isDirty) {
            val listener: (Event) -> Unit = { e ->
                e.preventDefault()
                e.asDynamic().returnValue = "You have unsaved changes."
                Unit
            }
            window.addEventListener("beforeunload", listener)
            onDispose { window.removeEventListener("beforeunload", listener) }
        } else {
            onDispose { }
        }
    }

    if (!loaded) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(Spacing.lg),
        ) {
            SkeletonBox(widthPx = 150, heightPx = 30, radiusPx = 8)
            SkeletonCard {
                Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
                    SkeletonLine(widthPct = 32, heightPx = 14)
                    SkeletonFormFields(fields = 2)
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("gap", "20px")
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            ) {
                Box(modifier = Modifier.styleModifier {
                    property("flex", "3 1 460px")
                    property("min-width", "0")
                    property("display", "flex")
                }) {
                    SkeletonCard {
                        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
                            SkeletonLine(widthPct = 28, heightPx = 14)
                            SkeletonBlock(heightPx = 320, radiusPx = 10)
                        }
                    }
                }
                Box(modifier = Modifier.styleModifier {
                    property("flex", "2 1 360px")
                    property("min-width", "0")
                    property("display", "flex")
                }) {
                    SkeletonCard {
                        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
                            SkeletonLine(widthPct = 24, heightPx = 14)
                            SkeletonBlock(heightPx = 320, radiusPx = 10)
                        }
                    }
                }
            }
        }
        return
    }

    val sectionsError = (SectionsConfigValidator.validate(sectionsConfig) as? ValidationResult.Error)?.message
    val editable = !isDefault
    val showPresets = mode is TemplateEditorMode.Create && editable

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.lg),
    ) {
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Button(
                label = "← Back to templates",
                variant = ButtonVariant.Ghost,
                onClick = {
                    if (!isDirty || window.confirm("You have unsaved changes. Leave anyway?")) {
                        suppressGuard = true
                        ctx.router.navigateTo("/app/templates")
                    }
                },
            )
        }

        if (isDefault) {
            DefaultLockBanner(onDuplicate = {
                ctx.router.navigateTo("/app/templates/new?from=default")
            })
        }

        if (showPresets) {
            TemplatePresetStrip(
                activePresetId = activePresetId,
                onPick = { preset ->
                    activePresetId = preset.id
                    if (name.isBlank()) name = preset.name
                    if (description.isBlank()) description = preset.description
                    promptBody = preset.promptBody
                    sectionsConfig = preset.sectionsConfig
                },
                onClear = {
                    activePresetId = null
                    promptBody = ""
                    sectionsConfig = SectionsConfigDto.DEFAULT
                },
            )
        }

        MetadataCard(
            name = name,
            onNameChange = { name = it },
            description = description,
            onDescriptionChange = { description = it },
            sectionsConfig = sectionsConfig,
            onSectionsConfigChange = { sectionsConfig = it },
            sectionsError = sectionsError,
            enabled = editable,
        )

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("gap", "20px")
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
        ) {
            Box(
                modifier = Modifier.styleModifier {
                    property("flex", "3 1 460px")
                    property("min-width", "0")
                    property("display", "flex")
                },
            ) {
                PromptBodyCard(
                    promptBody = promptBody,
                    onPromptBodyChange = { promptBody = it },
                    enabled = editable,
                    onInsertVariable = { token ->
                        promptBody = insertAtCursor(promptBody, token)
                    },
                )
            }
            Box(
                modifier = Modifier.styleModifier {
                    property("flex", "2 1 360px")
                    property("min-width", "0")
                    property("display", "flex")
                },
            ) {
                TemplatePreviewPane(
                    promptBody = promptBody,
                    sectionsConfig = sectionsConfig,
                    workspaceName = workspaceName,
                )
            }
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "sticky")
                    property("bottom", "0")
                    property("background-color", "var(--sb-surface)")
                    property("border-top", "1px solid var(--sb-border-subtle)")
                    property("padding", "14px 0")
                    property("gap", "10px")
                    property("z-index", "20")
                },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            DirtyIndicator(isDirty = isDirty, isDefault = isDefault)
            Row(
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (isDefault) {
                    Button(
                        label = "Duplicate to edit",
                        variant = ButtonVariant.Primary,
                        onClick = { ctx.router.navigateTo("/app/templates/new?from=default") },
                    )
                } else {
                    Button(
                        label = "Cancel",
                        variant = ButtonVariant.Ghost,
                        enabled = !saving,
                        onClick = {
                            if (!isDirty || window.confirm("Discard unsaved changes?")) {
                                suppressGuard = true
                                ctx.router.navigateTo("/app/templates")
                            }
                        },
                    )
                    Button(
                        label = "Test Fire",
                        variant = ButtonVariant.Secondary,
                        enabled = promptBody.isNotBlank(),
                        onClick = { testFireOpen = true },
                    )
                    Button(
                        label = if (saving) "Saving…" else "Save",
                        variant = ButtonVariant.Primary,
                        enabled = !saving && name.isNotBlank() && promptBody.isNotBlank() && sectionsError == null && isDirty,
                        onClick = {
                            saving = true
                            scope.launch {
                                val res = when (mode) {
                                    is TemplateEditorMode.Create -> store.create(
                                        workspaceId,
                                        CreateTemplateRequestDto(
                                            name = name.trim(),
                                            description = description.trim().ifBlank { null },
                                            promptBody = promptBody,
                                            sectionsConfig = sectionsConfig,
                                        ),
                                    )
                                    is TemplateEditorMode.Edit -> store.update(
                                        workspaceId,
                                        mode.templateId,
                                        UpdateTemplateRequestDto(
                                            name = name.trim(),
                                            description = description.trim().ifBlank { null },
                                            promptBody = promptBody,
                                            sectionsConfig = sectionsConfig,
                                        ),
                                    )
                                }
                                saving = false
                                when (res) {
                                    is Result.Ok -> {
                                        ui.showToast("Template saved.", ToastKind.Success)
                                        suppressGuard = true
                                        ctx.router.navigateTo("/app/templates")
                                    }
                                    is Result.Err -> ui.showToast(
                                        "Save failed: ${res.error.displayMessage}",
                                        ToastKind.Error,
                                    )
                                }
                            }
                        },
                    )
                }
            }
        }
    }

    if (testFireOpen) {
        TestFireModal(
            workspaceId = workspaceId,
            sectionsConfig = sectionsConfig,
            teamsStore = teamsStore,
            store = store,
            onDismiss = { testFireOpen = false },
        )
    }
}
